import { t } from '@/common/utils/translations'
import { placeTooltip, type TooltipBox } from './tooltipPlacement'

const ROOT_ID = 'messages-tooltip-root'
const STYLE_ID = 'messages-tooltip-style'

let anchorEl: HTMLElement | null = null
let anchorName = ''
let rootEl: HTMLElement | null = null
let onPointerDown: ((event: Event) => void) | null = null
let onKeyDown: ((event: KeyboardEvent) => void) | null = null
let onReposition: (() => void) | null = null

/** Button (or other control) whose script is running. showTooltip anchors here when no name is passed. */
export function rememberTooltipAnchor(el: HTMLElement | null, name?: string): void {
  anchorEl = el
  anchorName = String(name ?? '').trim()
}

let retryTimer: ReturnType<typeof setTimeout> | null = null
let showGeneration = 0

const TOOLTIP_RETRY_MS = 50
const TOOLTIP_RETRY_LIMIT = 120

function cancelTooltipRetry(): void {
  if (retryTimer != null) {
    clearTimeout(retryTimer)
    retryTimer = null
  }
}

function dismissVisibleTooltip(): void {
  if (typeof document !== 'undefined') {
    if (onPointerDown) document.removeEventListener('pointerdown', onPointerDown, true)
    if (onKeyDown) document.removeEventListener('keydown', onKeyDown, true)
    if (onReposition) {
      window.removeEventListener('resize', onReposition)
      window.removeEventListener('scroll', onReposition, true)
    }
  }
  onPointerDown = null
  onKeyDown = null
  onReposition = null
  rootEl?.remove()
  rootEl = null
}

export function hideTooltipPopup(): void {
  showGeneration += 1
  cancelTooltipRetry()
  dismissVisibleTooltip()
}

function ensureStyle(): void {
  if (document.getElementById(STYLE_ID)) return
  const style = document.createElement('style')
  style.id = STYLE_ID
  style.textContent = `
.messages-tooltip {
  position: fixed;
  z-index: 28000;
  box-sizing: border-box;
  max-width: min(22rem, calc(100vw - 16px));
  background: #ffffff;
  color: #1f2937;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(31, 41, 55, 0.16);
  padding: 0.55rem 1.8rem 0.65rem 0.75rem;
  font-size: 13px;
  line-height: 1.4;
}
.messages-tooltip__title {
  font-weight: 600;
  margin-bottom: 0.25rem;
  overflow-wrap: anywhere;
}
.messages-tooltip__body {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  max-height: 16rem;
  overflow: auto;
}
.messages-tooltip__close {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 22px;
  height: 22px;
  padding: 0;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
}
.messages-tooltip__close:hover {
  background: #f3f4f6;
  color: #1f2937;
}
.messages-tooltip__arrow {
  position: absolute;
  width: 10px;
  height: 10px;
  background: #ffffff;
  border-left: 1px solid #d0d5dd;
  border-top: 1px solid #d0d5dd;
}
.messages-tooltip[data-placement='below'] .messages-tooltip__arrow {
  top: -6px;
  transform: rotate(45deg);
}
.messages-tooltip[data-placement='above'] .messages-tooltip__arrow {
  bottom: -6px;
  transform: rotate(225deg);
}
.messages-tooltip[data-placement='float'] .messages-tooltip__arrow {
  display: none;
}
`
  document.head.appendChild(style)
}

function escapeAttr(value: string): string {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') return CSS.escape(value)
  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')
}

function findVisibleControl(name: string): HTMLElement | null {
  const selector = `[data-platform-control="${escapeAttr(name)}"], [data-field-name="${escapeAttr(name)}"]`
  const nodes = document.querySelectorAll(selector)
  for (const node of nodes) {
    if (!(node instanceof HTMLElement)) continue
    const rect = node.getBoundingClientRect()
    if (rect.width > 0 && rect.height > 0) return node
  }
  return null
}

function resolveAnchor(explicitName: string): HTMLElement | null {
  const named = explicitName.trim()
  if (named) {
    const found = findVisibleControl(named)
    if (found) return found
  }
  if (anchorEl?.isConnected) {
    const rect = anchorEl.getBoundingClientRect()
    if (rect.width > 0 && rect.height > 0) return anchorEl
  }
  if (anchorName) {
    const found = findVisibleControl(anchorName)
    if (found) return found
  }
  return null
}

function readBox(el: HTMLElement | null): TooltipBox | null {
  if (!el) return null
  const rect = el.getBoundingClientRect()
  return {
    left: rect.left,
    top: rect.top,
    width: rect.width,
    height: rect.height,
  }
}

function applyPosition(root: HTMLElement, anchor: HTMLElement | null): void {
  const popup = root.getBoundingClientRect()
  const placed = placeTooltip(readBox(anchor), { width: popup.width, height: popup.height }, {
    width: window.innerWidth,
    height: window.innerHeight,
  })
  root.style.top = `${Math.round(placed.top)}px`
  root.style.left = `${Math.round(placed.left)}px`
  root.dataset.placement = placed.placement
  const arrow = root.querySelector<HTMLElement>('.messages-tooltip__arrow')
  if (!arrow) return
  if (!anchor || placed.placement === 'float') {
    arrow.style.left = ''
    return
  }
  const anchorBox = anchor.getBoundingClientRect()
  const center = anchorBox.left + anchorBox.width / 2
  const raw = center - placed.left - 5
  const maxLeft = Math.max(12, popup.width - 22)
  arrow.style.left = `${Math.round(Math.min(maxLeft, Math.max(12, raw)))}px`
}

/**
 * Non-modal hint. Not a Messages dialog: no mask, script does not wait.
 * anchorName matches data-platform-control (action) or data-field-name.
 * onLoad runs before the form is mounted: a named anchor is waited for, not floated.
 */
export function showTooltipPopup(message: string, title = '', anchorName = ''): void {
  if (typeof document === 'undefined') return
  const text = String(message ?? '').trim()
  if (!text) {
    hideTooltipPopup()
    return
  }
  const generation = ++showGeneration
  cancelTooltipRetry()
  dismissVisibleTooltip()
  attemptShowTooltip(text, String(title ?? ''), String(anchorName ?? ''), generation, 0)
}

function attemptShowTooltip(
  text: string,
  title: string,
  explicitName: string,
  generation: number,
  attempt: number,
): void {
  if (generation !== showGeneration || typeof document === 'undefined') return
  const named = explicitName.trim()
  const anchor = named ? findVisibleControl(named) : resolveAnchor('')
  if (named && !anchor && attempt < TOOLTIP_RETRY_LIMIT) {
    retryTimer = setTimeout(() => {
      retryTimer = null
      attemptShowTooltip(text, title, explicitName, generation, attempt + 1)
    }, TOOLTIP_RETRY_MS)
    return
  }
  if (named && !anchor) return
  mountTooltip(text, title, anchor, named)
}

function mountTooltip(
  text: string,
  title: string,
  anchor: HTMLElement | null,
  named: string,
): void {
  ensureStyle()
  let boundAnchor = anchor
  const root = document.createElement('div')
  root.id = ROOT_ID
  root.className = 'messages-tooltip'
  root.setAttribute('role', 'dialog')
  root.setAttribute('aria-modal', 'false')
  root.setAttribute('aria-label', (title || text).trim())

  const arrow = document.createElement('span')
  arrow.className = 'messages-tooltip__arrow'
  arrow.setAttribute('aria-hidden', 'true')
  root.appendChild(arrow)

  const trimmedTitle = String(title ?? '').trim()
  if (trimmedTitle) {
    const titleEl = document.createElement('div')
    titleEl.className = 'messages-tooltip__title'
    titleEl.textContent = trimmedTitle
    root.appendChild(titleEl)
  }

  const body = document.createElement('div')
  body.className = 'messages-tooltip__body'
  body.textContent = text
  root.appendChild(body)

  const close = document.createElement('button')
  close.type = 'button'
  close.className = 'messages-tooltip__close'
  close.setAttribute('aria-label', t('dialogs.close'))
  close.innerHTML =
    '<svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><path d="M2 2l8 8M10 2L2 10" stroke="currentColor" stroke-width="1.5"/></svg>'
  close.addEventListener('click', (event) => {
    event.preventDefault()
    event.stopPropagation()
    hideTooltipPopup()
  })
  root.appendChild(close)

  root.style.visibility = 'hidden'
  root.style.top = '0px'
  root.style.left = '0px'
  document.body.appendChild(root)
  rootEl = root
  applyPosition(root, boundAnchor)
  root.style.visibility = 'visible'

  onPointerDown = (event: Event) => {
    const target = event.target
    if (target instanceof Node && root.contains(target)) return
    hideTooltipPopup()
  }
  onKeyDown = (event: KeyboardEvent) => {
    if (event.key !== 'Escape') return
    event.preventDefault()
    event.stopPropagation()
    hideTooltipPopup()
  }
  onReposition = () => {
    if (!rootEl) return
    if (named && (!boundAnchor || !boundAnchor.isConnected)) {
      const next = findVisibleControl(named)
      if (next) boundAnchor = next
      else return
    } else if (boundAnchor && !boundAnchor.isConnected) {
      hideTooltipPopup()
      return
    }
    applyPosition(rootEl, boundAnchor)
  }
  document.addEventListener('pointerdown', onPointerDown, true)
  document.addEventListener('keydown', onKeyDown, true)
  window.addEventListener('resize', onReposition)
  window.addEventListener('scroll', onReposition, true)
}
